import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface ChatMessageProps {
  content: string;
  isUser: boolean;
  timestamp: Date;
  isThinking?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ 
  content, 
  isUser, 
  timestamp, 
  isThinking = false 
}) => {
  const { user } = useAuth();

  // Función para obtener las iniciales del usuario
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    
    const nameParts = user.name.trim().split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0][0].toUpperCase();
    }
    return 'U';
  };
  const formatMessage = (content: string) => {
    let formattedContent = content;
    
    // Process code blocks first (protect them)
    const codeBlocks: string[] = [];
    formattedContent = formattedContent.replace(/```(\w+)?\s*\n?([\s\S]*?)```/g, (match: string, lang: string, code: string) => {
      const blockId = `__CODE_BLOCK_${codeBlocks.length}__`;
      const langClass = lang ? `language-${lang}` : '';
      const cleanCode = code.trim();
      codeBlocks.push(`<pre class="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono my-4"><code class="${langClass}">${cleanCode}</code></pre>`);
      return blockId;
    });

    // Convert headers
    formattedContent = formattedContent.replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold mb-2 text-gray-900">$1</h3>');
    formattedContent = formattedContent.replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mb-3 text-gray-900">$1</h2>');
    formattedContent = formattedContent.replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mb-4 text-gray-900">$1</h1>');
    
    // Convert bold and italic
    formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');
    formattedContent = formattedContent.replace(/\*(.*?)\*/g, '<em class="italic text-gray-700">$1</em>');
    
    // Convert inline code
    formattedContent = formattedContent.replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');
    
    // Convert blockquotes
    formattedContent = formattedContent.replace(/^> (.*$)/gm, '<blockquote class="border-l-4 border-green-500 pl-4 my-4 italic text-gray-700 bg-green-50 py-2 rounded-r">$1</blockquote>');
    
    // Convert tables
    const tableRegex = /\|(.+)\|\n\|[-\s|]+\|\n((?:\|.+\|\n?)+)/g;
    formattedContent = formattedContent.replace(tableRegex, (match: string, header: string, body: string) => {
      const headerCells = header.split('|').filter((cell: string) => cell.trim()).map((cell: string) => 
        `<th class="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">${cell.trim()}</th>`
      ).join('');
      
      const bodyRows = body.split('\n').filter((row: string) => row.trim()).map((row: string) => {
        const cells = row.split('|').filter((cell: string) => cell.trim()).map((cell: string) => 
          `<td class="px-4 py-2 text-sm text-gray-900 border-b border-gray-100">${cell.trim()}</td>`
        ).join('');
        return `<tr class="hover:bg-gray-50 transition-colors duration-150">${cells}</tr>`;
      }).join('');
      
      return `
        <div class="overflow-x-auto my-4">
          <table class="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
            <thead class="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>${headerCells}</tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              ${bodyRows}
            </tbody>
          </table>
        </div>
      `;
    });
    
    // Convert lists - simple approach
    const lines = formattedContent.split('\n');
    let result: string[] = [];
    let currentList: string[] = [];
    let inList = false;
    let isOrderedList = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line.match(/^- (.+)$/)) {
        if (!inList) {
          inList = true;
          isOrderedList = false;
          currentList = [];
        }
        currentList.push(`<li class="mb-1 text-gray-700">${line.replace(/^- (.+)$/, '$1')}</li>`);
      } else if (line.match(/^\d+\. (.+)$/)) {
        if (!inList) {
          inList = true;
          isOrderedList = true;
          currentList = [];
        }
        currentList.push(`<li class="mb-1 text-gray-700">${line.replace(/^\d+\. (.+)$/, '$1')}</li>`);
      } else {
        if (inList && currentList.length > 0) {
          const listTag = isOrderedList ? 'ol' : 'ul';
          const listClass = isOrderedList ? 'list-decimal list-inside mb-3 text-gray-700 space-y-1' : 'list-disc list-inside mb-3 text-gray-700 space-y-1';
          result.push(`<${listTag} class="${listClass}">${currentList.join('')}</${listTag}>`);
          currentList = [];
          inList = false;
        }
        result.push(line);
      }
    }
    
    // Handle list at the end
    if (inList && currentList.length > 0) {
      const listTag = isOrderedList ? 'ol' : 'ul';
      const listClass = isOrderedList ? 'list-decimal list-inside mb-3 text-gray-700 space-y-1' : 'list-disc list-inside mb-3 text-gray-700 space-y-1';
      result.push(`<${listTag} class="${listClass}">${currentList.join('')}</${listTag}>`);
    }
    
    formattedContent = result.join('\n');
    
    // Convert paragraphs - only for lines that don't start with HTML tags
    formattedContent = formattedContent.replace(/^([^<\n].*)$/gm, '<p class="mb-3 text-gray-700 leading-relaxed">$1</p>');
    
    // Restore code blocks
    codeBlocks.forEach((block: string, index: number) => {
      formattedContent = formattedContent.replace(`__CODE_BLOCK_${index}__`, block);
    });
    
    return formattedContent;
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-6`}>
      <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start gap-2 max-w-4xl`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
          isUser 
            ? 'bg-blue-600 text-white' 
            : ''
        }`}>
          {isUser ? getUserInitials() : (
            <img 
              src={isThinking ? "/bsk-asistant-thinking-icon.png" : "/bsk-asistant-icon.png"} 
              alt={isThinking ? "BSK Assistant Thinking" : "BSK Assistant"} 
              className="w-12 h-10 rounded-full"
            />
          )}
        </div>
        
        {/* Message Bubble */}
        <div className={`flex-1 ${isUser ? 'text-right' : 'text-left'}`}>
          <div
            className={`inline-block rounded-2xl px-4 py-3 ${
              isUser
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                : isThinking
                ? 'bg-gray-100/80 backdrop-blur-sm text-gray-600'
                : 'bg-white/80 backdrop-blur-sm text-gray-900 shadow-md'
            }`}
          >
            {isThinking ? (
              <div className="flex items-center space-x-2">
                <span className="text-sm">{content}</span>
              </div>
            ) : isUser ? (
              <div className="whitespace-pre-wrap text-sm">{content}</div>
            ) : (
              <div 
                className="text-left"
                style={{ textAlign: 'left' }}
                dangerouslySetInnerHTML={{ __html: formatMessage(content) }}
              />
            )}
          </div>
          
          {/* Timestamp */}
          <div className={`text-xs mt-2 ${
            isUser ? 'text-gray-500 text-right' : 'text-gray-400'
          }`}>
            {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
